import { format } from 'date-fns';
import { AlertTriangle, Calendar, ChevronRight, Clock, Users } from 'lucide-react';
import type { PastDuePayrollPeriod } from '../../lib/payrollPastDuePeriods';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { payrollMinutesFromHours } from '../../lib/payrollHoursLabel';

interface PayrollPastDuePanelProps {
  periods: PastDuePayrollPeriod[];
  onSelectPeriod: (period: PastDuePayrollPeriod) => void;
}

function formatPastDueLabel(daysPastDue: number): string {
  if (daysPastDue === 1) return '1 day past due';
  return `${daysPastDue} days past due`;
}

export default function PayrollPastDuePanel({
  periods,
  onSelectPeriod,
}: PayrollPastDuePanelProps) {
  const hoursText = usePayrollHoursText(periods.map((period) => payrollMinutesFromHours(period.totalHours)));

  if (periods.length === 0) {
    return null;
  }

  const totalOutstanding = periods.reduce((sum, period) => sum + period.totalEarnings, 0);

  return (
    <div className="mb-3 sm:mb-4 bg-white rounded-2xl shadow-sm border border-amber-200/70 overflow-hidden">
      <div className="px-4 sm:px-5 py-4 border-b border-amber-100 bg-amber-50/80">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 sm:h-[18px] sm:w-[18px] text-amber-600 shrink-0" aria-hidden />
              <h2 className="text-[17px] sm:text-lg font-semibold text-gray-900 leading-tight">
                Past Due
              </h2>
            </div>
            <p className="mt-1 text-[13px] sm:text-sm text-amber-800/90">
              {periods.length} period{periods.length === 1 ? '' : 's'} with unpaid hours
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[11px] sm:text-xs font-medium uppercase tracking-wide text-amber-700/80">
              Outstanding
            </p>
            <p className="text-[22px] sm:text-2xl font-bold text-amber-900 leading-none tabular-nums mt-0.5">
              ${totalOutstanding.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-3 lg:p-4">
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 snap-x snap-mandatory">
          {periods.map((period) => (
              <button
                key={`${period.start.toISOString()}-${period.end.toISOString()}`}
                type="button"
                onClick={() => onSelectPeriod(period)}
                className="group w-[min(85vw,300px)] shrink-0 snap-start rounded-xl border border-amber-200/80 bg-white p-4 text-left transition-colors hover:border-amber-300 hover:bg-amber-50/40 active:bg-amber-50/60"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] sm:text-base font-semibold text-gray-900 leading-tight">
                      {format(period.start, 'MMM d')} – {format(period.end, 'MMM d, yyyy')}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-[12px] sm:text-[13px] text-amber-700 font-medium">
                      <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {formatPastDueLabel(period.daysPastDue)}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-amber-700 shrink-0 mt-0.5 transition-colors" />
                </div>

                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      Unpaid total
                    </p>
                    <p className="text-[22px] font-bold text-gray-900 leading-none tabular-nums mt-0.5">
                      ${period.totalEarnings.toFixed(2)}
                    </p>
                  </div>
                  {period.hasBeenRun ? (
                    <span className="inline-flex items-center rounded-md bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-800">
                      Partially paid
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                      Not run
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                    {period.resourceCount} {period.resourceCount === 1 ? 'resource' : 'resources'}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                    {hoursText(payrollMinutesFromHours(period.totalHours))}
                  </span>
                  <span>{period.unpaidEntryCount} unpaid entries</span>
                </div>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
